import React from 'react';
import { CalendarX2, Moon, Sun, Sunrise, Sunset } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EmptyState, Surface, Text } from '@/components/flup';
import SlotTile from './SlotTile';
import { GroupId, Slot, SlotFilter, getSlotView, groupSlots, matchesFilter } from './slotUtils';

/**
 * The day's slots, banded by time of day.
 *
 * Every band is open — a vendor's job here is scanning, and collapsed sections
 * hide exactly the thing they came to look at. The band header carries the
 * counts and a "select all open" shortcut, which is what the old per-group
 * "select multiple" checkbox was really trying to be.
 */

const GROUP_ICON: Record<GroupId, React.ComponentType<any>> = {
  morning: Sunrise,
  afternoon: Sun,
  evening: Sunset,
  midnight: Moon,
};

interface SlotBoardProps {
  slots: Slot[];
  selectedDate: Date;
  slotDurationInMinutes?: number;
  selectedIds: string[];
  filter: SlotFilter;
  onToggleSlot: (slot: Slot) => void;
  onSelectGroup: (slots: Slot[], selectAll: boolean) => void;
  /** Offered from the empty state when a filter has hidden everything. */
  onShowAll?: () => void;
}

const SlotBoard: React.FC<SlotBoardProps> = ({
  slots,
  selectedDate,
  slotDurationInMinutes,
  selectedIds,
  filter,
  onToggleSlot,
  onSelectGroup,
  onShowAll,
}) => {
  const selected = new Set(selectedIds);

  // Filter before grouping, so a band whose every slot is hidden drops out of
  // the board entirely rather than rendering as an empty card.
  const visible = React.useMemo(
    () =>
      slots.filter((slot) =>
        matchesFilter(getSlotView(slot, selectedDate, slotDurationInMinutes).state, filter),
      ),
    [slots, selectedDate, slotDurationInMinutes, filter],
  );

  const groups = groupSlots(visible);

  if (!slots.length) {
    return (
      <Surface padding={false}>
        <EmptyState
          icon={<CalendarX2 />}
          title="No slots on this date"
          message="This court has no schedule for the day you picked. Try another date, or add the court's timings in venue settings."
        />
      </Surface>
    );
  }

  // There are slots, but this filter hides all of them — say which filter, and
  // offer the way out rather than leaving a bare "nothing here".
  if (!visible.length) {
    return (
      <Surface padding={false}>
        <EmptyState
          icon={<CalendarX2 />}
          title={
            filter === 'available'
              ? 'No open slots left today'
              : filter === 'booked'
                ? 'Nothing booked on this date'
                : 'Every slot on this date has passed'
          }
          message={
            filter === 'upcoming'
              ? 'The whole day is over. Switch to All slots to review what happened, or pick a future date.'
              : 'Switch to All slots to see everything on this date, including passed and blocked slots.'
          }
          actionLabel={onShowAll ? 'Show all slots' : undefined}
          onAction={onShowAll}
        />
      </Surface>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map((group) => {
        const Icon = GROUP_ICON[group.id];
        const views = group.slots.map((slot) => ({
          slot,
          view: getSlotView(slot, selectedDate, slotDurationInMinutes),
        }));

        const selectable = views.filter((v) => v.view.selectable);
        const openCount = views.filter((v) => v.view.state === 'available').length;
        const bookedCount = views.filter((v) => v.view.state === 'booked').length;
        const shownCount = views.length;

        // The shortcut flips: once every actionable slot in the band is picked,
        // the same control clears them again.
        const allPicked =
          selectable.length > 0 && selectable.every((v) => selected.has(v.slot._id));

        return (
          <Surface key={group.id} padding="p-4 sm:p-5">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-3 min-w-0">
                <span className="grid place-items-center w-9 h-9 rounded-flup-sm bg-flup-surface-alt text-flup-ink shrink-0">
                  <Icon className="w-[18px] h-[18px]" aria-hidden />
                </span>
                <div className="min-w-0">
                  <Text variant="h3" as="p">{group.name}</Text>
                  <Text variant="micro" tone="tertiary" as="p" className="mt-0.5">
                    {group.timeRange}
                    {openCount > 0 ? ` · ${openCount} open` : ''}
                    {bookedCount > 0 ? ` · ${bookedCount} booked` : ''}
                    {openCount === 0 && bookedCount === 0 ? ` · ${shownCount} slots` : ''}
                  </Text>
                </div>
              </div>

              {selectable.length > 0 ? (
                <button
                  type="button"
                  onClick={() => onSelectGroup(selectable.map((v) => v.slot), !allPicked)}
                  className={cn(
                    'shrink-0 px-3 h-8 rounded-flup-xs border transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flup-brand',
                    allPicked
                      ? 'bg-flup-green border-flup-green text-white'
                      : 'bg-flup-surface border-flup-hairline text-flup-ink-2 hover:bg-flup-surface-alt hover:text-flup-ink',
                  )}
                >
                  <Text variant="micro" tone="inherit" as="span">
                    {allPicked ? 'Clear' : 'Select all'}
                  </Text>
                </button>
              ) : null}
            </div>

            <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
              {views.map(({ slot, view }) => (
                <SlotTile
                  key={slot._id || slot.start_time}
                  slot={slot}
                  view={view}
                  selected={selected.has(slot._id)}
                  onToggle={onToggleSlot}
                />
              ))}
            </div>
          </Surface>
        );
      })}
    </div>
  );
};

export default React.memo(SlotBoard);
